import { useNavigate, useLocation } from "react-router-dom"
import styles from "./sidebar.module.scss"
import { useCallback, useContext, useEffect, useState } from "react"
import { SidebarContext } from "./SidebarContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { NotificationContext } from "components/Notification/NotificationContext"
import { faBell, faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import { StudentNotification } from "shared/types/notificationTypes"
import { toast } from "react-toastify"
import { AuthContext } from "AuthContext"
import { updateStudentNotificationStatus } from "shared/routes/notifications/studentNotifications"
import studentRoute from "shared/routes/student"
import { SubmissionStudentState } from "pages/Dashboard/Student/Grades/Submission/SubmissionStudent"
import NotificationToastMessage from "components/Dashboards/Student/Notifications/NotificationToastMessage"
import ToolTip from "components/General/ToolTip"
import { Modal } from "react-bootstrap"
import { GuardedNavLink } from "components/General/GuardedNavLink"

export interface LinkGroupType {
    header: SidebarHeader | null
    links: (SidebarLink | "separator")[]
}

export type SidebarHeaderTitle = "STEM Sims™" | "Sci35™" | "Sci68™" | "Sci35MI™" | "Sci4Bio™" | "Sci4ESS™" | "eScience21™" | "Homeschool 35" | "Homeschool 68"

export interface SidebarHeader {
    title: SidebarHeaderTitle
    available: boolean
}

export interface SidebarLink {
    title: string
    path: string
    icon?: IconProp
    available: boolean
}

interface Props {
    linkGroups: LinkGroupType[]
}

const unavailableModalMessages: Record<SidebarHeaderTitle, string> = {
    "STEM Sims™": "STEM Sims™ provides lessons, assessments, and simulated laboratory experiments. To purchase, contact sales@stemsims.com.",
    "Sci35™": "Sci35™ provides science test prep for grades 3-5. To purchase, contact sales@stemsims.com.",
    "Sci68™": "Sci68™ provides science test prep for grades 6-8. To purchase, contact sales@stemsims.com.",
    "Sci4Bio™": "Sci4Bio™ provides science test prep for biology students. To purchase, contact sales@stemsims.com",
    "Sci4ESS™": "Sci4ESS™ provides science test prep for earth-space science students. To purchase, contact sales@stemsims.com.",
    "eScience21™": "eScience21™ provides an online version of the PNW BOCES Science 21 curriculum. To purchase, contact sales@stemsims.com.",
    "Homeschool 35": "Homeschool 35 provides science curriculum for grades 3-5. To purchase, contact sales@stemsims.com.",
    "Homeschool 68": "Homeschool 68 provides science curriculum for grades 6-8. To purchase, contact sales@stemsims.com.",
    "Sci35MI™": "Sci35MI™ provides science curriculum for grades 6-8. To purchase, contact sales@stemsims.com.",
}

export default function SidebarLinks({ linkGroups }: Props) {
    const { collapsed, toggleCollapse } = useContext(SidebarContext) // collapse sidebar when a sidebar link is clicked on Only If it's mobile screen
    const { isStudent } = useContext(AuthContext)

    const { notificationEvent } = useContext(NotificationContext)
    const [notificationReceived, setNotificationReceived] = useState<boolean>(false)

    const [disabledProductModal, setDisabledProductModal] = useState<SidebarHeaderTitle | null>(null)

    const navigate = useNavigate()
    const location = useLocation()

    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

    function onClickNavLink(link: SidebarLink) {
        if (!collapsed && window.innerWidth <= 800) {
            toggleCollapse()
        }
        if (link.title === "Notifications") {
            setNotificationReceived(false)
        }
    }

    const onClickHeader = (header: SidebarHeader) => {
        if (!header.available) {
            setDisabledProductModal(header.title)
            return
        }

        setCollapsedGroups(prev => {
            const newGroups = new Set(prev)

            if (newGroups.has(header.title)) {
                newGroups.delete(header.title)
            } else {
                newGroups.add(header.title)
            }

            return newGroups
        })
    }

    const openNotification = useCallback(async (notification: StudentNotification) => {
        const validNotificationTypes = ["submission-graded", "submission-rework", "submission-reject"] //there might be more types in the future
        if (!validNotificationTypes.includes(notification.type)) {
          return
        }
        await updateStudentNotificationStatus(notification.id, "read") // mark as read after click open notification
        setNotificationReceived(false)
    
        const assignment = await studentRoute.getAssignment(notification.assignment.id)
        if (notification.assignment && !assignment) {
          toast.error("There was an error opening the notification.")
          return
        }
    
        const submissionIndex = assignment.submissions.findIndex((submission) => submission.id === notification.submissionId)
        navigate("/dashboard/student/grades/submission", {
          state: {
              currentIndex: submissionIndex,
              assignment: assignment,
              submission: assignment.submissions[submissionIndex],
              studentId: notification.studentId,
              userType: "Student",
              previousPage: "/dashboard/student/notifications",
          } as SubmissionStudentState
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isStudent || !notificationEvent) return
        if (notificationEvent.type === "unread-notifications" || notificationEvent.type === "new-notification") {
            setNotificationReceived(true)
            if (notificationEvent.type === "new-notification" && notificationEvent.notification) {
                toast.info(
                    <NotificationToastMessage notificationEvent={notificationEvent} onOpenNotification={openNotification} closeToast={this} />, 
                    { icon: <FontAwesomeIcon icon={faBell} />, autoClose: 8000, closeOnClick: false, }
                )
            }
        }
    }, [isStudent, notificationEvent, openNotification])

    /* Default collapse link groups that are not available */
    useEffect(() => {
        if (linkGroups) {
            const defaultCollapsedGroups = linkGroups.filter(g => g.header !== null && !g.header.available).map(g => g.header.title)
            setCollapsedGroups(new Set(defaultCollapsedGroups))
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const shouldGuardLinks = isStudent && location.pathname === "/dashboard/student/assignments/assignment"

    return (<>
        <ul className={styles.sidebarLinks}>
            {linkGroups.map((linkGroup, index) => {
                const header = linkGroup.header
                const links = linkGroup.links
                const groupCollapsed = header && collapsedGroups.has(header.title)
                return (
                    <div>
                        {header && 
                        <p 
                            onClick={() => onClickHeader(header)} 
                            role="button" 
                            key={linkGroup.header.title + index} 
                            className={`fw-bold ps-4 bg-light p-2 rounded shadow-sm ${collapsed && "d-none"} ${!header.available && "text-muted"}`}>
                                <span>{linkGroup.header.title}</span>
                                <i className={`fas ${groupCollapsed ? `fa-circle-chevron-right` : `fa-circle-chevron-down`} float-end fs-4 ${header.available ? "text-primary" : "text-muted"}`} />
                        </p>}
                        <div className={`${(groupCollapsed && !collapsed) ? styles.collapsedLinkGroup : styles.expandedLinkGroup}`}>
                            {
                                links.map((link, linkIndex) => 
                                    <Link 
                                        onClickNavLink={onClickNavLink} 
                                        link={link} 
                                        index={linkIndex} 
                                        collapsed={collapsed} 
                                        notificationReceived={notificationReceived}
                                        shouldGuard={shouldGuardLinks} />)
                            }
                        </div>
                    </div>
                )
            })}
        </ul>
    <Modal size="lg" show={!!disabledProductModal} onHide={() => setDisabledProductModal(null)}>
        <Modal.Header closeButton>
        </Modal.Header>

        <Modal.Body>
           { unavailableModalMessages[disabledProductModal] || "This feature is not available on your current plan. To purchase, contact sales@stemsims.com." }
        </Modal.Body>

        <Modal.Footer>
        </Modal.Footer>
    </Modal>
    </>)
}


interface LinkProps {
    onClickNavLink: (link: SidebarLink) => void
    link: SidebarLink | "separator"
    index: number
    collapsed: boolean
    notificationReceived: boolean
    shouldGuard: boolean
}

const Link = ({ onClickNavLink, link, index, collapsed, notificationReceived, shouldGuard }: LinkProps) => {

    if (link === "separator") {
        return <hr key={`separator-${index}`} />
    }

    const isNotificationLink = link.title === "Notifications"

    if (!link.available) {
        return (
            <div>
                <ToolTip key={link.title + index} title="This feature is not available on your current plan">
                    <div>
                        <li className={`${styles.unavailableSidebarLink} ${collapsed ? styles.collapsedLink : ""} ${isNotificationLink && !notificationReceived && "text-secondary"}`}>
                            {link.icon && <FontAwesomeIcon icon={link.icon} className={collapsed ? "" : "me-2"} />}
                            {collapsed ? "" : link.title}
                            {isNotificationLink && notificationReceived && <FontAwesomeIcon icon={faCircleExclamation} className="text-primary ms-1 position-absolute" size="lg" />}
                        </li>
                    </div>
                </ToolTip>
            </div>
        )
    }

    return (
        <div>
            <GuardedNavLink to={link.path} shouldGuard={shouldGuard} key={link.title + link.path} onClick={() => onClickNavLink(link)}>
                {
                    ({ isActive }) =>
                        <li className={`${isActive ? styles.active : ""} ${collapsed ? styles.collapsedLink : ""} ${isNotificationLink && !notificationReceived && "text-secondary"}`}>
                            {link.icon && <FontAwesomeIcon icon={link.icon} className={collapsed ? "" : "me-2"} />}
                            {collapsed ? "" : link.title}
                            {isNotificationLink && notificationReceived && <FontAwesomeIcon icon={faCircleExclamation} className="text-primary ms-1 position-absolute" size="lg" />}
                        </li>
                }
            </GuardedNavLink>
        </div>
    )
    
}
